// Static UI constants for the Audits module: upload limits, select option lists,
// the status enum, and label maps. No backend/mock data lives here anymore — the
// module talks to the real API via campaignAuditsApi.js. Option lists remain
// placeholders where the BE enums aren't final yet.

// File types accepted by the "Upload documents" dragger (drives the `accept`
// attribute).
export const SUPPORTED_UPLOAD_FORMATS = [
  '.csv',
  '.docx',
  '.html',
  '.json',
  '.md',
  '.pdf',
  '.ppt',
  '.txt',
  '.xlsx',
];

// Human-readable list rendered in the upload helper text.
export const SUPPORTED_FORMAT_LABEL = 'CSV, DOCX, HTML, JSON, MD, PDF, PPT, TXT, and XLSX';

// Endpoint limits (BE spec): at most 10 files, 5MB each.
export const MAX_UPLOAD_FILES = 10;
export const MAX_UPLOAD_SIZE_MB = 5;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

// Only used to render the "will take approximately X" estimate copy.
export const SECONDS_PER_FILE_ESTIMATE = 15;

// ─── Campaign-audit status enum (mirrors the backend) ────────────────────────
export const AUDIT_STATUS = {
  PENDING: 'pending',
  NEEDS_INFO: 'needs_info',
  READY_FOR_CONFIRMATION: 'ready_for_confirmation',
  CONFIRMED: 'confirmed',
  PROCESSING: 'processing',
  ANALYZED: 'analyzed',
  FAILED: 'failed',
  ARCHIVED: 'archived',
};

// Maps a completeness.missing[].field to the banner's label + description.
// Falls back to the API's humanPrompt for fields not listed here.
export const MISSING_FIELD_LABELS = {
  subject: { label: 'Subject of the campaign', description: 'Subject name, description or URL' },
  budget: { label: 'Budget', description: 'How much was allocated and spent' },
  audience: { label: 'Audience', description: 'Demographic your campaign aimed to reach' },
  influencers: { label: 'Influencers', description: 'Their platforms, spend, and outcomes' },
};

// ─── Step 2 "Confirm" — select options ────────────────────────────────────────

// Platform options (placeholder pending BE enums).
export const PLATFORM_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'website', label: 'Website / Other' },
];

// Outcome sub-fields for an influencer (chip label + full label for the modal).
export const OUTCOME_FIELDS = [
  { key: 'views', label: 'Views or Impressions', chipLabel: 'Views' },
  { key: 'clicks', label: 'Clicks', chipLabel: 'Clicks' },
  { key: 'conversions', label: 'Conversions', chipLabel: 'Conv' },
  { key: 'engagements', label: 'Engagements', chipLabel: 'Eng' },
];

// Option lists are placeholders pending the backend enums.
export const SUBJECT_TYPE_OPTIONS = [
  { value: 'product', label: 'A product' },
  { value: 'service', label: 'A service' },
  { value: 'business', label: 'A business' },
  { value: 'event', label: 'An event' },
  { value: 'other', label: 'Other subject type, please specify:' },
];

export const OBJECTIVE_OPTIONS = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'sales', label: 'Sales' },
];

export const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD' },
  { value: 'cad', label: 'CAD' },
  { value: 'eur', label: 'EUR' },
  { value: 'gbp', label: 'GBP' },
  { value: 'aud', label: 'AUD' },
  { value: 'jpy', label: 'JPY' },
  { value: 'inr', label: 'INR' },
  { value: 'brl', label: 'BRL' },
  { value: 'mxn', label: 'MXN' },
  { value: 'sgd', label: 'SGD' },
];

// ─── Report — 05 Network mix copy (Figma 844:17873) ─────────────────────────

// Platforms on the "Actual vs. Recommended spend by platform" chart, in display order.
// Reddit stays off until further exploration (AAPU-7089 out of scope).
export const NETWORK_SPEND_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'linkedin'];

export const NETWORK_SPEND_COPY = {
  TITLE: 'Actual vs. Recommended spend by platform',
  SUBTITLE: (budget) =>
    `Using your same ${budget} budget, here’s how we would reshuffle your spendings`,
  LEGEND_ACTUAL: 'Actual',
  LEGEND_RECOMMENDED: 'Recommended',
  STATUS: { new: 'New', pause: 'Pause' },
  FORMATS_TITLE: 'Content format by platform',
  TOTAL_POSTS: (count) => `Total: ${count} posts`,
  INSIGHT_LABEL: 'KEY INSIGHT',
  NO_FORMATS: 'No content-format data available.',
  NO_PLACEMENT: 'Best placement not available',
};
